import React, { useState, useEffect } from 'react';
import { formatDate } from '../../utils/dateUtils';
import {
  X,
  Calendar,
  DollarSign,
  Receipt,
  CheckCircle,
  Loader,
  AlertCircle,
  Store,
  TrendingUp,
  Building2
} from 'lucide-react';
import apiService from '../../services/api';
import { useToast } from '../../hooks/useToast';
import BaseModal from '../common/BaseModal';

/**
 * Unified modal for linking receipts and transactions in both directions:
 * - Transaction → Receipt: Pass transaction prop to show matching receipts
 * - Receipt → Transaction: Pass receipt prop to show matching transactions
 */
const LinkModal = ({ isOpen, onClose, transaction, receipt, onLinked }) => {
  const [allMatches, setAllMatches] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [linking, setLinking] = useState(false);
  const { showToast, ToastContainer } = useToast();

  // Filter states
  const [amountTolerance, setAmountTolerance] = useState(0); // 0% default (exact match)
  const [dateRange, setDateRange] = useState(7); // ±7 days default
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [accounts, setAccounts] = useState([]);

  // Determine mode based on which prop is provided
  const mode = transaction ? 'receipt' : 'transaction';
  const isReceiptMode = mode === 'receipt';

  useEffect(() => {
    if (isOpen && (transaction || receipt)) {
      loadMatches();
      if (!isReceiptMode) {
        // Load accounts for account filter (only for transaction mode)
        loadAccounts();
      }
    }
  }, [isOpen, transaction, receipt]);

  // Apply filters whenever filter state changes
  useEffect(() => {
    applyFilters();
  }, [allMatches, amountTolerance, dateRange, selectedAccount]);

  const loadAccounts = async () => {
    try {
      const response = await apiService.getAccounts();
      setAccounts(response || []);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const loadMatches = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isReceiptMode) {
        // Transaction → Receipt: Use backend API for matching receipts
        const response = await apiService.getMatchingReceipts(transaction.id);
        const matchList = response.matches || [];
        setAllMatches(matchList);
      } else {
        // Receipt → Transaction: Use backend API for matching transactions
        const response = await apiService.getMatchingTransactions(receipt.id);
        const matchList = response.matches || [];
        setAllMatches(matchList);
      }
    } catch (err) {
      console.error(`Error loading matching ${mode}s:`, err);
      setError(`Failed to load matching ${mode}s`);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allMatches];

    // Amount tolerance filter (percentage-based)
    filtered = filtered.filter(match => {
      const amountDiff = match.amount_diff || 0;
      const baseAmount = isReceiptMode ? match.total_amount : (receipt ? receipt.total_amount : 0);
      if (baseAmount === 0) return true; // Avoid division by zero

      const percentageDiff = (amountDiff / baseAmount) * 100;
      return percentageDiff <= amountTolerance;
    });

    // Date range filter (skip if "Any date" is selected)
    if (dateRange !== 999999) {
      filtered = filtered.filter(match => {
        const dateDiff = isReceiptMode ? match.date_diff : match.date_diff_days;
        return dateDiff <= dateRange;
      });
    }

    // Account filter (only for transaction mode)
    if (!isReceiptMode && selectedAccount !== 'all') {
      filtered = filtered.filter(match => match.account_name === selectedAccount);
    }

    setMatches(filtered);
  };

  const handleLink = async (id) => {
    try {
      setLinking(true);
      setError(null);

      let response;
      if (isReceiptMode) {
        // Link receipt to transaction
        response = await apiService.linkReceiptToTransaction(id, transaction.id);
      } else {
        // Link receipt to transaction (same API call, different parameter order)
        response = await apiService.linkReceiptToTransaction(receipt.id, id);
      }

      // Show success message with tip info
      if (response.calculated_tip && response.calculated_tip > 0) {
        showToast(`Linked successfully!\n\nCalculated Tip: $${response.calculated_tip.toFixed(2)} (${response.tip_percentage.toFixed(1)}%)`, 'success');
      } else {
        showToast('Linked successfully!', 'success');
      }

      onLinked?.();
      onClose();
    } catch (err) {
      console.error('Error linking:', err);
      setError('Failed to link');
    } finally {
      setLinking(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount || 0);
  };

  // formatDate imported from utils/dateUtils

  const getScoreColor = (score) => {
    if (score >= 15) return 'text-green-600 bg-green-50 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
    if (score >= 10) return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800';
    return 'text-gray-600 bg-gray-50 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600';
  };

  const getScoreLabel = (score) => {
    if (score >= 15) return 'Excellent Match';
    if (score >= 10) return 'Good Match';
    return 'Possible Match';
  };

  // Generate modal title and subtitle based on mode
  const getModalTitle = () => {
    if (isReceiptMode) {
      return 'Link Receipt to Transaction';
    }
    return 'Match to Transaction';
  };

  const getModalSubtitle = () => {
    if (isReceiptMode && transaction) {
      return `${transaction.description} - ${formatCurrency(Math.abs(transaction.total_amount), transaction.currency)}`;
    }
    if (!isReceiptMode && receipt) {
      return `${receipt.merchant} - ${formatCurrency(receipt.total_amount)} on ${formatDate(receipt.receipt_date)}`;
    }
    return '';
  };

  const getEmptyMessage = () => {
    if (isReceiptMode) {
      return {
        title: 'No matching receipts found',
        description: 'No receipts found within ±7 days that could match this transaction',
        hint: 'Receipt amount must be ≤ transaction amount to account for tips'
      };
    }

    // Check if receipt date is in the future
    const isFutureDate = receipt && new Date(receipt.receipt_date) > new Date();

    return {
      title: 'No Matches Found',
      description: isFutureDate
        ? `Receipt date (${formatDate(receipt.receipt_date)}) is in the future. This is likely an OCR error.`
        : "We couldn't find any transactions that match this receipt within ±7 days.",
      hint: isFutureDate
        ? 'Try editing the receipt to fix the date, then try matching again.'
        : (receipt ? `Transaction amount must be at least ${formatCurrency(receipt.total_amount)} to account for tip.` : '')
    };
  };

  const renderMatchCard = (match) => {
    if (isReceiptMode) {
      // Receipt card (when linking receipt to transaction)
      return (
        <div
          key={match.id}
          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        >
          {/* Desktop layout */}
          <div className="hidden md:flex items-start justify-between gap-4">
            <div className="flex-1">
              {/* Receipt info */}
              <div className="flex items-center gap-3 mb-2">
                <Store className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {match.merchant}
                  </h3>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(match.receipt_date)}
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {formatCurrency(match.total_amount, match.currency)}
                    </div>
                    {match.items_count > 0 && (
                      <span className="text-xs">
                        {match.items_count} item{match.items_count !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Match details */}
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Receipt Amount</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(match.total_amount)}
                    </p>
                  </div>
                  {match.potential_tip > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Calculated Tip</p>
                      <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(match.potential_tip)} ({match.tip_percentage.toFixed(1)}%)
                      </p>
                    </div>
                  )}
                </div>
                {match.date_diff > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {match.date_diff} day{match.date_diff !== 1 ? 's' : ''} difference
                  </p>
                )}
              </div>
            </div>

            {/* Match score badge */}
            <div className="flex-shrink-0">
              <div className={`px-3 py-1.5 rounded-md border ${getScoreColor(match.match_score)}`}>
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5" />
                  <div className="text-xs font-semibold">{match.match_score.toFixed(1)}</div>
                </div>
                <div className="text-[10px] mt-0.5 font-medium">{getScoreLabel(match.match_score)}</div>
              </div>
            </div>

            {/* Link button */}
            <button
              onClick={() => handleLink(match.id)}
              disabled={linking}
              className="ml-4 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              {linking ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Linking...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Link
                </>
              )}
            </button>
          </div>

          {/* Mobile layout */}
          <div className="md:hidden space-y-3">
            {/* Receipt info */}
            <div className="flex items-start gap-3">
              <Store className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                  {match.merchant}
                </h3>
                <div className="flex flex-col gap-1 mt-1 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{formatDate(match.receipt_date)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4 flex-shrink-0" />
                    <span>{formatCurrency(match.total_amount, match.currency)}</span>
                  </div>
                </div>
              </div>
              {/* Match score badge - mobile position */}
              <div className="flex-shrink-0">
                <div className={`px-2 py-1 rounded-md border ${getScoreColor(match.match_score)}`}>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    <div className="text-xs font-semibold">{match.match_score.toFixed(1)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Match details - mobile */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Receipt</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(match.total_amount)}
                </p>
              </div>
              {match.potential_tip > 0 && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Tip</p>
                  <p className="font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(match.potential_tip)} ({match.tip_percentage.toFixed(1)}%)
                  </p>
                </div>
              )}
            </div>

            {/* Link button - mobile full width */}
            <button
              onClick={() => handleLink(match.id)}
              disabled={linking}
              className="w-full px-4 py-2.5 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {linking ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Linking...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Link Receipt</span>
                </>
              )}
            </button>
          </div>
        </div>
      );
    } else {
      // Transaction card (when linking transaction to receipt)
      return (
        <div
          key={match.transaction_id}
          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        >
          {/* Desktop layout */}
          <div className="hidden md:flex items-start justify-between gap-4">
            <div className="flex-1">
              {/* Transaction info */}
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {match.description}
                  </h3>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(match.transaction_date)}
                      {match.date_diff_days > 0 && (
                        <span className="text-gray-400 ml-1">
                          ({match.date_diff_days} day{match.date_diff_days !== 1 ? 's' : ''} diff)
                        </span>
                      )}
                    </div>
                    <span className="text-xs truncate">{match.account_name}</span>
                  </div>
                </div>
              </div>

              {/* Match details */}
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Transaction Amount</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(Math.abs(match.amount))}
                    </p>
                  </div>
                  {match.potential_tip > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Calculated Tip</p>
                      <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(match.potential_tip)} ({match.tip_percentage.toFixed(1)}%)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Match score badge */}
            <div className="flex-shrink-0">
              <div className={`px-3 py-1.5 rounded-md border ${getScoreColor(match.match_score)}`}>
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5" />
                  <div className="text-xs font-semibold">{match.match_score.toFixed(1)}</div>
                </div>
                <div className="text-[10px] mt-0.5 font-medium">{getScoreLabel(match.match_score)}</div>
              </div>
            </div>

            {/* Link button */}
            <button
              onClick={() => handleLink(match.transaction_id)}
              disabled={linking}
              className="ml-4 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              {linking ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Linking...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Link
                </>
              )}
            </button>
          </div>

          {/* Mobile layout */}
          <div className="md:hidden space-y-3">
            {/* Transaction info */}
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                  {match.description}
                </h3>
                <div className="flex flex-col gap-1 mt-1 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">
                      {formatDate(match.transaction_date)}
                      {match.date_diff_days > 0 && ` (${match.date_diff_days}d)`}
                    </span>
                  </div>
                  <div className="text-xs truncate">{match.account_name}</div>
                </div>
              </div>
              {/* Match score badge - mobile position */}
              <div className="flex-shrink-0">
                <div className={`px-2 py-1 rounded-md border ${getScoreColor(match.match_score)}`}>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    <div className="text-xs font-semibold">{match.match_score.toFixed(1)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Match details - mobile */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Transaction</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(Math.abs(match.amount))}
                </p>
              </div>
              {match.potential_tip > 0 && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Tip</p>
                  <p className="font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(match.potential_tip)} ({match.tip_percentage.toFixed(1)}%)
                  </p>
                </div>
              )}
            </div>

            {/* Link button - mobile full width */}
            <button
              onClick={() => handleLink(match.transaction_id)}
              disabled={linking}
              className="w-full px-4 py-2.5 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {linking ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Linking...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Link Transaction</span>
                </>
              )}
            </button>
          </div>
        </div>
      );
    }
  };

  const emptyState = getEmptyMessage();

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} size="xl" showCloseButton={false}>
      {/* Custom Header with subtitle and X button */}
      <div className="px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">{getModalTitle()}</h2>
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
            {getModalSubtitle()}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-pink-600" />
            <span className="ml-3 text-gray-600 dark:text-gray-400">
              Finding matching {mode}s...
            </span>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Filters - Show when we have matches OR when there are no matches (to help users understand why) */}
            {(allMatches.length > 0 || matches.length === 0) && (
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="space-y-4">
                  {/* Amount Tolerance Slider */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Amount tolerance: {amountTolerance === 0 ? 'Exact match' : `±${amountTolerance}%`}
                      </label>
                      {amountTolerance > 0 && (
                        <button
                          onClick={() => setAmountTolerance(0)}
                          className="text-xs text-pink-600 dark:text-pink-400 hover:underline"
                        >
                          Reset to exact
                        </button>
                      )}
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      step="1"
                      value={amountTolerance}
                      onChange={(e) => setAmountTolerance(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-pink-600"
                      style={{
                        background: `linear-gradient(to right, rgb(219 39 119) 0%, rgb(219 39 119) ${amountTolerance * 2}%, rgb(229 231 235) ${amountTolerance * 2}%, rgb(229 231 235) 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>0%</span>
                      <span>25%</span>
                      <span>50%</span>
                    </div>
                  </div>

                  {/* Date Range and Account Filters */}
                  <div className="flex flex-wrap gap-4">
                    {/* Date Range Selector */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 dark:text-gray-300">Date range:</label>
                      <select
                        value={dateRange}
                        onChange={(e) => setDateRange(Number(e.target.value))}
                        className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-pink-500 focus:border-pink-500"
                      >
                        <option value={0}>Same day</option>
                        <option value={1}>±1 day</option>
                        <option value={3}>±3 days</option>
                        <option value={7}>±7 days</option>
                        <option value={14}>±14 days</option>
                        <option value={30}>±30 days</option>
                        <option value={90}>±90 days</option>
                        <option value={999999}>Any date (ignore date)</option>
                      </select>
                    </div>

                    {/* Account Filter (only for transaction mode) */}
                    {!isReceiptMode && accounts.length > 0 && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700 dark:text-gray-300">Account:</label>
                        <select
                          value={selectedAccount}
                          onChange={(e) => setSelectedAccount(e.target.value)}
                          className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-pink-500 focus:border-pink-500"
                        >
                          <option value="all">All accounts</option>
                          {accounts.map(account => (
                            <option key={account.id} value={account.name}>
                              {account.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Results count */}
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Showing {matches.length} of {allMatches.length} matches
                  </div>
                </div>
              </div>
            )}

            {/* Match results or empty state */}
            {matches.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                  {allMatches.length === 0 ? emptyState.title : 'No matches with current filters'}
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {allMatches.length === 0 ? emptyState.description : 'Try adjusting the filters above to see more matches.'}
                </p>
                {allMatches.length === 0 && emptyState.hint && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    {emptyState.hint}
                  </p>
                )}
              </div>
            ) : (
              matches.map(renderMatchCard)
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm md:text-base"
        >
          Cancel
        </button>
      </div>

      <ToastContainer />
    </BaseModal>
  );
};

export default LinkModal;
